import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, RotateCw, Rocket } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Skeleton,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from '../WaapEmptyCard';
import {
  useWaapApiProtectionOverviewCharts,
  useWaapDiscoverySampling,
  API_PROTECTION_TIME_RANGES,
  type ApiProtectionTimeRange,
  type WaapChart,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WaapChartWidget } from '../dashboard/WaapChartWidget';
import styles from './ApiProtectionPage.module.css';

/**
 * API Protection — Management Overview rendered as NATIVE charts (no Superset
 * iframe; the lead's convention). The BE pre-normalizes the customer's BI board
 * (Superset dashboard 58) into typed widgets (`useWaapApiProtectionOverviewCharts`),
 * which we group by kind: scalar KPIs, then pie/table breakdowns, then the
 * Request Trend time-series.
 *
 * Unlike the Dashboard overview, this board has a user-selectable Time Range; the
 * BE forwards it as the dashboard's "time_range" template filter. Account-level:
 * the selected website's id is only the handle the BE uses to resolve the customer.
 */
export function ApiProtectionPage() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  // Account-level: API Protection resolves the same Conversant customer from any
  // of the account's websites, so we use a representative domain automatically
  // (no website picker, no "select a website" gate — like AI-WAF / Bot Management).
  const { selected, isLoading: wsLoading } = useWaapWebsite();
  const domainId = selected?.domainId ?? '';
  const [range, setRange] = useState<ApiProtectionTimeRange>('Last 24 Hours');
  // API Protection is "on" once request/response sampling is enabled (Discovery →
  // Sampling) — that's the switch that feeds every API-Protection board. Until then
  // the Management dashboard has no data, so we show an enable CTA instead of blank.
  const { data: sampling, isLoading: samplingLoading } = useWaapDiscoverySampling(domainId);
  const apiProtectionEnabled = sampling?.enabled ?? false;
  const { data, isLoading, isError, error, refetch, isFetching } = useWaapApiProtectionOverviewCharts(
    domainId,
    range,
  );

  const groups = useMemo(() => {
    const charts = data?.charts ?? [];
    const numbers: WaapChart[] = [];
    const split: WaapChart[] = [];
    const wide: WaapChart[] = [];
    for (const c of charts) {
      if (c.kind === 'number') numbers.push(c);
      else if (c.kind === 'timeseries') wide.push(c);
      else split.push(c); // pie + table
    }
    return { numbers, split, wide };
  }, [data]);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'API Protection' }]} />}
        title="API Protection"
      />

      {wsLoading || samplingLoading ? (
        <Skeleton height={480} />
      ) : !apiProtectionEnabled ? (
        <WaapEmptyCard
          icon={<ShieldCheck size={20} />}
          title="API Protection is not enabled"
          description="Turn on API request/response sampling to start discovering, monitoring and protecting your APIs. Analytics will appear here once it's collecting data."
          actions={
            <Button
              startIcon={<Rocket size={16} />}
              onClick={gate('apiProtection', 'API Protection', () =>
                navigate(`/services/waap/${serviceId}/api-protection/discovery`),
              )}
            >
              Enable API Protection
            </Button>
          }
        />
      ) : (
        <>
          <div className={styles.toolbar}>
            <span className={styles.rangeLabel}>Time Range</span>
            <Select value={range} onValueChange={(v) => setRange(v as ApiProtectionTimeRange)}>
              <SelectTrigger className={styles.rangeSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {API_PROTECTION_TIME_RANGES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              onClick={() => refetch()}
              disabled={isFetching}
              className={styles.refreshBtn}
            >
              <RotateCw size={14} />
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <Skeleton height={480} />
          ) : data === null ? (
            <WaapEmptyCard
              icon={<ShieldCheck size={20} />}
              title="API Protection analytics not configured"
              description="The analytics board is not set up for this environment yet."
            />
          ) : isError ? (
            <Alert tone="danger" title="Couldn't load API Protection">
              {errMessage(error)}
            </Alert>
          ) : (
            <div className={styles.grids}>
              {groups.numbers.length > 0 && (
                <div className={styles.numberGrid}>
                  {groups.numbers.map((c) => (
                    <WaapChartWidget key={c.key} chart={c} />
                  ))}
                </div>
              )}
              {groups.split.length > 0 && (
                <div className={styles.splitGrid}>
                  {groups.split.map((c) => (
                    <WaapChartWidget key={c.key} chart={c} />
                  ))}
                </div>
              )}
              {groups.wide.length > 0 && (
                <div className={styles.wideGrid}>
                  {groups.wide.map((c) => (
                    <WaapChartWidget key={c.key} chart={c} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {upgradeDialog}
    </Container>
  );
}
